// src/components/tutor/availability/tutorAvailabilityFirestore.js
import {
  collection,
  doc,
  getDocs,
  query,
  where,
  orderBy,
  setDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  Timestamp,
} from "firebase/firestore";
import { db } from "../../../firebaseConfig";

const BLOCKS = "tutor_availability_blocks";

function hasOwn(obj, key) {
  return Object.prototype.hasOwnProperty.call(obj || {}, key);
}

function isScheduleDayOverrideDoc(id, data = {}) {
  if (typeof id === "string" && id.startsWith("day_")) return true;
  if (data?.source === "schedule_day_settings") return true;
  if (hasOwn(data, "unavailable") || hasOwn(data, "hidden")) return true;
  return false;
}

function toDateMaybe(ts) {
  if (!ts) return null;
  if (ts instanceof Date) return ts;
  if (typeof ts?.toDate === "function") return ts.toDate();
  return null;
}

function timestampToMillis(value) {
  if (!value) return 0;
  if (value instanceof Date) return value.getTime();
  if (typeof value?.toDate === "function") return value.toDate().getTime();
  if (typeof value?.seconds === "number") return value.seconds * 1000;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : 0;
}

function dateKeyLocal(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function shouldReplaceAvailabilityRecord(current, candidate) {
  if (!current) return true;

  const currentIsOverride = isScheduleDayOverrideDoc(current?.id, current);
  const candidateIsOverride = isScheduleDayOverrideDoc(candidate?.id, candidate);
  if (candidateIsOverride !== currentIsOverride) {
    return candidateIsOverride;
  }

  const currentUpdated = timestampToMillis(current?.updatedAt);
  const candidateUpdated = timestampToMillis(candidate?.updatedAt);
  if (candidateUpdated !== currentUpdated) {
    return candidateUpdated > currentUpdated;
  }

  const currentStart = toDateMaybe(current?.start)?.getTime() || 0;
  const candidateStart = toDateMaybe(candidate?.start)?.getTime() || 0;
  return candidateStart > currentStart;
}

// Tutor-only: fetch blocks for THIS tutor in [start, end)
export async function fetchTutorBlocksInRange(uid, rangeStartDate, rangeEndDateExclusive) {
  const startTs = Timestamp.fromDate(rangeStartDate);
  const endTs = Timestamp.fromDate(rangeEndDateExclusive);

  const q = query(
    collection(db, BLOCKS),
    where("uid", "==", uid),
    where("start", ">=", startTs),
    where("start", "<", endTs),
    orderBy("start", "asc")
  );

  const snap = await getDocs(q);
  const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

  const byDay = new Map();
  rows.forEach((row) => {
    const start = toDateMaybe(row?.start);
    if (!start || !row?.uid) return;
    const key = `${row.uid}|${dateKeyLocal(start)}`;
    const current = byDay.get(key);
    if (shouldReplaceAvailabilityRecord(current, row)) {
      byDay.set(key, row);
    }
  });

  return [...byDay.values()]
    .filter((row) => !row?.unavailable)
    .sort((a, b) => {
      const aStart = toDateMaybe(a?.start)?.getTime() || 0;
      const bStart = toDateMaybe(b?.start)?.getTime() || 0;
      return aStart - bStart;
    });
}

export async function createTutorAvailabilityBlock({ uid, startDate, endDate }) {
  const dayBase = new Date(startDate);
  dayBase.setHours(0, 0, 0, 0);
  const docId = `day_${dateKeyLocal(dayBase)}_${uid}`;

  await setDoc(
    doc(db, BLOCKS, docId),
    {
      uid,
      start: Timestamp.fromDate(startDate),
      end: Timestamp.fromDate(endDate),
      startMin: startDate.getHours() * 60 + startDate.getMinutes(),
      endMin: endDate.getHours() * 60 + endDate.getMinutes(),
      unavailable: false,
      hidden: false,
      source: "schedule_day_settings",
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );

  return docId;
}

export async function updateTutorAvailabilityBlock(id, { startDate, endDate }) {
  const patch = {
    start: Timestamp.fromDate(startDate),
    end: Timestamp.fromDate(endDate),
    startMin: startDate.getHours() * 60 + startDate.getMinutes(),
    endMin: endDate.getHours() * 60 + endDate.getMinutes(),
    updatedAt: serverTimestamp(),
  };
  if (typeof id === "string" && id.startsWith("day_")) {
    patch.unavailable = false;
    patch.hidden = false;
    patch.source = "schedule_day_settings";
  }

  await updateDoc(doc(db, BLOCKS, id), patch);
}

export async function deleteTutorAvailabilityBlock(id) {
  if (typeof id === "string" && id.startsWith("day_")) {
    await updateDoc(doc(db, BLOCKS, id), {
      unavailable: true,
      hidden: true,
      source: "schedule_day_settings",
      updatedAt: serverTimestamp(),
    });
    return;
  }

  await deleteDoc(doc(db, BLOCKS, id));
}
